import React, {
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import WaveSurfer from "wavesurfer.js";

export const AudioWavePlayer = forwardRef(({ audioUrl, onTimeUpdate }, ref) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentTime, setCurrentTime] = useState("00:00:000"); // Format: MM:SS:MMM

  // Format time in MM:SS:MMM
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  // Expose seekTo function to parent components
  useImperativeHandle(ref, () => ({
    seekTo: (time) => {
      if (wavesurferRef.current && isInitialized) {
        try {
          wavesurferRef.current.seekTo(
            time / wavesurferRef.current.getDuration()
          );
          if (!isPlaying) {
            wavesurferRef.current.play();
          }
        } catch (err) {
          setError(`Seek error: ${err.message}`);
          console.error("Seek error:", err);
        }
      }
    },
  }));


  useEffect(() => {
    if (!waveformRef.current) return;

    let isMounted = true;

    const initializeWaveSurfer = () => {
      if (!isMounted || wavesurferRef.current) return;


      wavesurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: "rgb(229 231 235)",
        progressColor: "rgb(75 85 99)",
        height: 50,
        barWidth: 2,
        responsive: true,
      });

      // Load audio
      try {
        wavesurferRef.current.load(audioUrl);
      } catch (err) {
        if (isMounted) {
          setError(`Failed to load audio: ${err.message}`);
          console.error("WaveSurfer load error:", err);
        }
      }

      // Event listeners
      wavesurferRef.current.on("play", () => {
        if (isMounted) setIsPlaying(true);
      });
      wavesurferRef.current.on("pause", () => {
        if (isMounted) setIsPlaying(false);
      });
      wavesurferRef.current.on("error", (err) => {
        if (isMounted && !err.message.includes("AbortError")) {
          setError(`WaveSurfer error: ${err.message || err}`);
          console.error("WaveSurfer error:", err);
        } else {
          console.log("Ignored AbortError:", err);
        }
      });
      wavesurferRef.current.on("ready", () => {
        if (isMounted) {
          setError(null);
          setIsInitialized(true);
          console.log("WaveSurfer ready");
        }
      });
      // Update current time
      wavesurferRef.current.on("timeupdate", (time) => {
        if (isMounted) {
          setCurrentTime(formatTime(time));
          onTimeUpdate(time);
        }
      });
    };

    // Delay initialization to avoid Strict Mode issues
    const timer = setTimeout(initializeWaveSurfer, 0);

    // Cleanup
    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.destroy();
        } catch (err) {
          console.warn("Error during WaveSurfer cleanup:", err);
        }
      }
    };
  }, [audioUrl]);

  const togglePlayPause = () => {
    if (wavesurferRef.current && isInitialized) {
      try {
        wavesurferRef.current.playPause();
      } catch (err) {
        setError(`Playback error: ${err.message}`);
        console.error("Playback error:", err);
      }
    }
  };

  return (
    <div>
      {error && (
        <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>
      )}
      {/* <div style={{ marginBottom: '10px' }}>
        Time: {currentTime}
      </div> */}

      <div className="flex flex-row justify-center py-2 gap-4">
        <button
          onClick={togglePlayPause}
          disabled={!isInitialized}
          className="mx-auto"
        >
          {isPlaying ? (
            <img alt="" src="/icons/pause.svg" className="h-6 w-6" />
          ) : (
            <img alt="" src="/icons/play.svg" className="h-6 w-6" />
          )}
        </button>
        <div ref={waveformRef} style={{ minHeight: "50px", width: "100%" }} />
      </div>
    </div>
  );
});
